import { WithPuckProps } from '@measured/puck';
import { DEMOGRAPHICS_FIELDS } from '@psyomics/ui-common';

import { DemographicsBar } from '../../../../../components/molecules/demographics-bar/demographics-bar';
import { useRenderContext } from '../../../context/render-context';
import { getReportMetadata } from '../../../report-types';
import { Answers } from '../../../survey-types';
import * as createBlockUtils from '../../../utils/create-block';
import { getDisplayValues } from '../../../utils/surveys/questions';

type DemographicsBarBlockProps = {
  patientIdLabel?: string;
};
const { DATE_OF_BIRTH, ETHNIC_GROUP, FIRST_NAME, GENDER, LAST_NAME, SEX } = DEMOGRAPHICS_FIELDS;

const getName = (answers?: Answers): string | undefined => {
  return `${answers?.[FIRST_NAME] ?? ''} ${answers?.[LAST_NAME] ?? ''}`.trim() || undefined;
};

const DemographicsBarBlockRender = ({
  patientIdLabel = 'Patient ID',
  puck,
}: WithPuckProps<DemographicsBarBlockProps>) => {
  const { survey, allAnswers } = useRenderContext();

  const respondentType = 'patient';
  const answers = allAnswers?.[respondentType];
  const [ethnicity] = getDisplayValues(survey, ETHNIC_GROUP);
  const [gender] = getDisplayValues(survey, GENDER);
  const [sex] = getDisplayValues(survey, SEX);

  const metadata = getReportMetadata(puck.metadata);

  if (!metadata) return null;

  const {
    healthcareSystemPatientId,
    healthcareSystemLookupId,
    hasLookedUpHealthcareSystemId,
    piSystemId,
    respondents,
  } = metadata;

  const patientCompleted = respondents.some((r) => r.respondentType === respondentType && r.complete);

  return (
    <DemographicsBar
      dateOfBirth={answers?.[DATE_OF_BIRTH] ? new Date(String(answers[DATE_OF_BIRTH])) : undefined}
      ethnicity={ethnicity}
      gender={gender}
      name={getName(answers)}
      patientId={{
        label: patientIdLabel,
        value: healthcareSystemPatientId,
      }}
      patientIdLookup={healthcareSystemLookupId}
      piSystemId={piSystemId}
      sex={sex}
      useHealthcareLookupId={hasLookedUpHealthcareSystemId && patientCompleted}
    />
  );
};

const createDemographicsBarBlock = createBlockUtils.makeBlockFactory<DemographicsBarBlockProps>(
  {
    withExampleAnswersJson: true,
    overrides: { selectedRespondentType: 'patient' },
    requireSurvey: false,
  },
  {
    label: 'Demographics Bar',
    render: DemographicsBarBlockRender,
    fields: {
      patientIdLabel: {
        type: 'text',
        label: 'Patient ID Label (e.g., NHS Number, Patient ID)',
      },
    },
  }
);

export { createDemographicsBarBlock };
